/*
 * CAPTURE-OWNED MODULE
 * Similar existing document types for a name the user is typing, and the one
 * case that blocks creation: a name whose key already exists.
 *
 * Decisions 9 to 12, decisions/capture/2026-10-17-template-studio-field-model-redesign.md.
 * Uses the field matcher (utils/fieldMatch) with a document-type generic word
 * list: "Tax Form" must not list W-4 Form and I-9 Form through "form" alone.
 * The shared synonyms are kept (PO reads as Purchase Order here too).
 *
 * The similar list WARNS and never blocks. The key collision BLOCKS, because
 * POST /templates/document-types derives the key from the label and
 * (tenant_id, key) is unique: the create would 409.
 */
import { matchNames } from './fieldMatch';

/**
 * Words too common to make a partial match on their own in a multi-word
 * document type name (see GENERIC_WORDS in fieldMatch for the rule).
 */
export const DOC_TYPE_GENERIC_WORDS = Object.freeze(['document', 'form', 'report', 'notice', 'type']);

/**
 * The key the server will write for this label. A verbatim copy of slugify in
 * capture-app/src/services/setup/fieldKey.ts (no shared code across the
 * service boundary): lowercase, strip all but a-z, 0-9 and whitespace, trim,
 * collapse whitespace runs to "_". Not normalizeName: "I-9 Form" is i9_form
 * here and i_9_form there, and only this one predicts a 409.
 */
export function docTypeKeyFor(label) {
  return String(label ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_');
}

/**
 * @param {object} args
 * @param {string} args.label     what the user typed
 * @param {Array}  args.docTypes  GET /templates/document-types rows, active AND inactive
 * @returns {{
 *   derivedKey: string,
 *   keyEmpty: boolean,          the name has text but no letter or digit (the server refuses it)
 *   collision: object|null,     the existing type whose key equals derivedKey
 *   matches: Array<{ item, tier: 'exact'|'contained'|'partial', active: boolean, system: boolean }>
 * }}
 */
export function findDocTypeSuggestions({ label, docTypes }) {
  const text = String(label ?? '').trim();
  const derivedKey = docTypeKeyFor(text);
  const none = { derivedKey, keyEmpty: false, collision: null, matches: [] };
  if (!text) return none;
  if (!derivedKey) return { ...none, keyEmpty: true };

  const rows = (docTypes || []).filter((dt) => dt && dt.key);
  // Keys are unique per tenant, but a global row and a tenant row may share
  // one; the active one is the one to offer.
  const colliding = rows.filter((dt) => dt.key === derivedKey);
  const collision = colliding.find((dt) => dt.is_active) || colliding[0] || null;

  const shape = (item, tier) => ({ item, tier, active: !!item.is_active, system: !!item.is_system });
  const matches = matchNames({ label: text }, rows, { genericWords: DOC_TYPE_GENERIC_WORDS })
    .map((m) => shape(m.item, m.tier));

  // The colliding type always leads the list, as exact, even where the word
  // matcher reads the name differently ("I-9" is words i, 9; its key is i9).
  if (collision) {
    const rest = matches.filter((m) => m.item !== collision);
    return { derivedKey, keyEmpty: false, collision, matches: [shape(collision, 'exact'), ...rest] };
  }
  return { derivedKey, keyEmpty: false, collision: null, matches };
}
